import type {Bc3Preview} from "./bc3";
export type Bc3PlannedResource = {
 code:string;label:string;unit:string;quantity:number;unit_price:number;partial:number;
 declaredDates:string[];source_code?:string;auxiliary?:boolean;
};
export type Bc3AuxiliaryComposition = {
 code:string;label:string;unit:string;description:string;declaredPrice:number;
 calculatedPrice:number;declaredDates:string[];resources:Bc3PlannedResource[];
};
export type Bc3PlannedArticle = {
 code:string;name:string;unit:string;description:string;chapterPath:string[];
 declaredPrice:number|null;calculatedPrice:number|null;declaredDates:string[];
 resources:Bc3PlannedResource[];auxiliaryCompositions?:Bc3AuxiliaryComposition[];blockers:string[];
};
export type Bc3ImportPlan = {format:"l37_bc3_plan_v1";tariffIndex:number;source:{owner:string;version:string;encoding?:string;declaredEncoding?:string};
 chapters:number;sharedResources:number;articles:Bc3PlannedArticle[];blockers:string[]};
const round=(value:number)=>Math.round((value+Number.EPSILON)*100)/100;
type Resolved = {resources:Bc3PlannedResource[];auxiliaryCompositions:Bc3AuxiliaryComposition[];
 total:number|null;blockers:string[];height:number};
export function prepareBc3Import(preview:Bc3Preview,tariffIndex:number):Bc3ImportPlan {
 if(!Number.isInteger(tariffIndex) || tariffIndex<0 || tariffIndex>99)throw new Error("Escolha uma tarifa válida.");
 const concepts=new Map(preview.concepts.map(concept=>[concept.code,concept]));
 const children=new Map<string,typeof preview.links>();const parents=new Map<string,typeof preview.links>();
 for(const link of preview.links){
  const outgoing=children.get(link.parent);if(outgoing)outgoing.push(link);else children.set(link.parent,[link]);
  const incoming=parents.get(link.child);if(incoming)incoming.push(link);else parents.set(link.child,[link]);
 }
 function chapterPath(code:string,seen=new Set<string>()):string[]|null {
  if(seen.has(code) || seen.size>100)return null;
  const concept=concepts.get(code);if(!concept?.chapter)return null;
  const ancestors=(parents.get(code)??[]).filter(link=>concepts.get(link.parent)?.chapter);
  if(ancestors.length>1)return null;
  if(!ancestors.length)return [concept.summary || concept.code];
  const prefix=chapterPath(ancestors[0].parent,new Set([...seen,code]));
  return prefix ? [...prefix,concept.summary || concept.code] : null;
 }
 const memo=new Map<string,Resolved>();
 function resolve(code:string,seen=new Set<string>()):Resolved {
  const failure=(message:string):Resolved=>({resources:[],auxiliaryCompositions:[],total:null,blockers:[message],height:101});
  if(seen.has(code))return failure("Composição auxiliar cíclica: "+code);
  if(seen.size>=100)return failure("Composição auxiliar excede 100 níveis: "+code);
  const cached=memo.get(code);if(cached)return cached;
  const concept=concepts.get(code);if(!concept || concept.chapter)return failure("Conceito auxiliar inválido: "+code);
  const links=children.get(code)??[];const blockers:string[]=[];const used=new Set<string>();
  const resources:Bc3PlannedResource[]=[];const auxiliary=new Map<string,Bc3AuxiliaryComposition>();
  let height=1;
  const firstPercent=links.findIndex(link=>{
    const resource=concepts.get(link.child);
    return !!resource && (resource.unit==="%" || /[%&]/.test(resource.code));
  });
  const percentTail=firstPercent>=0 ? links.slice(firstPercent) : [];
  const tailAllPercent=percentTail.every(item=>concepts.get(item.child)?.unit==="%");
  const nestedComposition=seen.size>0;
  if(!concept.unit.trim())blockers.push("Unidade do artigo em falta.");
  if(!links.length || links.length>500)blockers.push("Artigo sem composição ou com mais de 500 recursos: necessita de revisão manual.");
  for(const [index,link] of links.entries()){
   const resource=concepts.get(link.child);
   if(!resource){blockers.push("Recurso em falta: "+link.child);continue;}
   if(used.has(link.child)){blockers.push("Recurso repetido na composição: "+link.child);continue;}
   used.add(link.child);
   if(resource.chapter){blockers.push("Capítulo dentro de uma composição: "+link.child);continue;}
   if(link.percentageCodes?.length){blockers.push("Base percentual explícita por validar: "+link.child);continue;}
   if(firstPercent>=0 && index>=firstPercent && !tailAllPercent){
    blockers.push("Base percentual por validar: "+link.child);continue;
   }
   if(resource.unit==="%" || /[%&]/.test(resource.code)){
    const group=percentTail;
    const position=index-firstPercent;
    const role=group.length===1 ? "complement"
     : !nestedComposition && group.length===2 && position===0 ? "auxiliary"
     : !nestedComposition && group.length===2 && position===1 ? "indirect" : "";
    const label=(resource.summary || "").toLocaleLowerCase("pt-PT");
    const saysAuxiliary=label.includes("auxiliar");
    const saysIndirect=label.includes("indirect") || label.includes("indireto");
    const contradicted=(role==="auxiliary" && saysIndirect && !saysAuxiliary) || (role==="indirect" && saysAuxiliary && !saysIndirect);
    if(!role || !/^%[^%&]*$/.test(resource.code) || resource.unit!=="%" || firstPercent<=0
     || link.factor!==1 || link.yield===null || link.yield<0 || link.yield>1
     || resources.length!==index || used.has(role) || contradicted || (children.get(link.child)??[]).length){
     blockers.push("Base percentual por validar: "+link.child);continue;
    }
    const base=round(resources.reduce((sum,line)=>sum+line.partial,0));
    resources.push({code:role,source_code:resource.code,label:resource.summary || resource.code,unit:"%",
     quantity:link.yield*100,unit_price:base,partial:round(base*link.yield),declaredDates:[...resource.dates]});
    used.add(role);
    continue;
   }
   const quantity=link.factor===null || link.yield===null ? null : link.factor*link.yield;
   let price=resource.prices[tariffIndex]??null;const isAuxiliary=!!children.get(link.child)?.length;
   if(isAuxiliary){
    const nested=resolve(link.child,new Set([...seen,code]));
    height=Math.max(height,nested.height+1);
    if(nested.blockers.length || nested.total===null || price===null){
     blockers.push("Composição auxiliar "+link.child+": "+(nested.blockers.join(" · ") || "preço declarado em falta"));continue;
    }
    price=nested.total;
    for(const item of nested.auxiliaryCompositions)auxiliary.set(item.code,item);
    auxiliary.set(resource.code,{code:resource.code,label:resource.summary || resource.code,unit:resource.unit,
     description:resource.description,declaredPrice:resource.prices[tariffIndex]!,calculatedPrice:price,
     declaredDates:[...resource.dates],resources:nested.resources});
   }
   if(!resource.unit.trim() || price===null || quantity===null || !Number.isFinite(quantity)
    || quantity<0 || quantity>1e9 || price<0 || price>1e9){
    blockers.push("Preço, unidade ou consumo inválido: "+link.child);continue;
   }
   resources.push({code:resource.code,label:resource.summary || resource.code,unit:resource.unit,
    quantity,unit_price:price,partial:round(quantity*price),declaredDates:[...resource.dates],...(isAuxiliary ? {auxiliary:true} : {})});
  }
  if(height>100 || auxiliary.size>1000)blockers.push("Composição auxiliar excede os limites de importação.");
  const total=resources.length===links.length && links.length ? round(resources.reduce((sum,line)=>sum+line.partial,0)) : null;
  const declaredPrice=concept.prices[tariffIndex]??null;
  if(declaredPrice===null)blockers.push("Preço do artigo em falta na tarifa escolhida.");
  if(declaredPrice!==null && total!==null && Math.abs(round(declaredPrice-total))>0.01)
   blockers.push("A composição não reproduz o preço declarado; confirmar arredondamentos e custos adicionais.");
  const result={resources,auxiliaryCompositions:[...auxiliary.values()],total,blockers,height};memo.set(code,result);return result;
 }
 const candidates=preview.concepts.filter(concept=>!concept.chapter &&
  (parents.get(concept.code)??[]).some(link=>concepts.get(link.parent)?.chapter));
 const articles:Bc3PlannedArticle[]=candidates.map(concept=>{
  const composition=resolve(concept.code);const blockers=[...composition.blockers];
  const chapterLinks=(parents.get(concept.code)??[]).filter(link=>concepts.get(link.parent)?.chapter);
  const path=chapterLinks.length===1 ? chapterPath(chapterLinks[0].parent) : null;
  if(!path)blockers.push("Hierarquia de capítulos ambígua ou inválida.");
  return {code:concept.code,name:concept.summary,unit:concept.unit,description:concept.description,
   chapterPath:path??[],declaredPrice:concept.prices[tariffIndex]??null,calculatedPrice:composition.total,
   declaredDates:[...concept.dates],resources:composition.resources,blockers,
   ...(composition.auxiliaryCompositions.length ? {auxiliaryCompositions:composition.auxiliaryCompositions} : {})};
 });
 const usage=new Map<string,number>();
 for(const article of articles)for(const resource of article.resources)if(resource.unit!=="%")usage.set(resource.code,(usage.get(resource.code)??0)+1);
 return {format:"l37_bc3_plan_v1",tariffIndex,source:{owner:preview.owner,version:preview.version,encoding:preview.decodedEncoding??preview.encoding,declaredEncoding:preview.encoding},
  chapters:preview.concepts.filter(concept=>concept.chapter).length,
  sharedResources:[...usage.values()].filter(count=>count>1).length,articles,
  blockers:[...preview.warnings,...(!articles.length ? ["Nenhum artigo ligado a capítulos foi identificado."] : [])]};
}
